import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Textarea } from "../../components/Textarea/Textarea";
import { Table, type Column } from "../../components/Table/Table";
import { useToast } from "../../components/Toast/toastContext";
import {
  createPhrase,
  deletePhrase,
  importPhrases,
  listPhrases,
  updatePhrase,
  botError,
} from "./api";
import { ago } from "./relativeTime";
import styles from "./BotsPage.module.css";
import type { ImportResult, Phrase } from "./types";

/** Below this many phrases a category starts repeating itself audibly. */
const THIN_CATEGORY = 8;

/** The human name of each platform category. thanks:<type> is generated. */
const CATEGORY_NAMES: Record<string, string> = {
  small_talk: "Small talk",
  welcome: "Welcome newcomer",
  reaction: "Reaction to a player",
  joined: "Greeting on join",
};

function categoryName(key: string): string {
  if (CATEGORY_NAMES[key]) return CATEGORY_NAMES[key];
  if (key.startsWith("thanks:")) return `Thanks · ${key.slice("thanks:".length)}`;
  return key;
}

/** What each category is FOR — the difference between a bot that decorates a
 *  guild and one that answers something nobody asked. */
const CATEGORY_BLURB: Record<string, string> = {
  small_talk: "background chatter — never a reply to anything",
  welcome: "said to a living player who has just joined",
  reaction: "a short remark after a real player speaks, never an answer",
  joined: "said by the bot itself right after it joins a guild",
};

/**
 * The Phrases tab: everything the bots can say.
 *
 * The platform generates none of it and never leaves the deployment to fetch
 * any — where the operator got the text is their business, and an import is
 * the only way in. That is also why a phrase has two states rather than four:
 * drafts only ever existed to proof-read generated text.
 */
export function PhrasesTab({ projectID }: { projectID: string }) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [category, setCategory] = useState<string | undefined>();
  const [locale, setLocale] = useState<string | undefined>();
  const [draft, setDraft] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["project", projectID, "bots", "phrases", category, locale],
    queryFn: () => listPhrases(projectID, category, locale),
    enabled: !!projectID,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["project", projectID, "bots"] });
  const onError = (err: unknown) =>
    push({ variant: "danger", message: botError(err) });

  const add = useMutation({
    mutationFn: (text: string) =>
      createPhrase(projectID, data!.category, data!.locale, text),
    onSuccess: () => {
      setDraft("");
      void invalidate();
    },
    onError,
  });
  const patch = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "live" | "paused" }) =>
      updatePhrase(projectID, id, { status }),
    onSuccess: () => void invalidate(),
    onError,
  });
  const drop = useMutation({
    mutationFn: (id: string) => deletePhrase(projectID, id),
    onSuccess: () => void invalidate(),
    onError,
  });

  const activeCategory = data?.category ?? "";
  const activeLocale = data?.locale ?? "";
  const countIn = (cat: string, loc: string) =>
    data?.coverage.find((c) => c.category === cat && c.locale === loc)?.count ?? 0;

  const columns: Column<Phrase>[] = [
    { key: "text", header: "Phrase", cell: (p) => <span className={styles.phrase}>{p.text}</span> },
    {
      key: "used",
      header: "Used · 24h",
      width: "110px",
      cell: (p) => <span className={styles.mono}>{p.used_24h}</span>,
    },
    {
      key: "last",
      header: "Last used",
      width: "130px",
      cell: (p) => <span className={styles.mono}>{ago(p.last_used_at)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      cell: (p) => (
        <Badge variant={p.status === "live" ? "success" : "neutral"} dot>
          {p.status}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "170px",
      cell: (p) => (
        <div className={styles.rowActions}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              patch.mutate({
                id: p.id,
                status: p.status === "live" ? "paused" : "live",
              })
            }
          >
            {p.status === "live" ? "Pause" : "Resume"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => drop.mutate(p.id)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={styles.toolbar}>
        {data && data.locales.length > 0 && (
          <SegmentedToggle
            value={activeLocale}
            onChange={setLocale}
            aria-label="Locale"
            options={data.locales.map((l) => ({
              value: l,
              label: `${l} ${countIn(activeCategory, l)}`,
            }))}
          />
        )}
        <span className={styles.spacer} />
        <Button variant="secondary" onClick={() => setImportOpen(true)}>
          ↑ Import list
        </Button>
      </div>

      <div className={styles.split}>
        <div className={styles.rail}>
          <div className={styles.railHead}>Category</div>
          {(data?.categories ?? []).map((c) => {
            const n = countIn(c, activeLocale);
            return (
              <button
                key={c}
                type="button"
                className={`${styles.railItem} ${
                  c === activeCategory ? styles.railActive : ""
                } ${n === 0 ? styles.railWarn : ""}`}
                onClick={() => setCategory(c)}
              >
                <span>
                  {categoryName(c)}
                  <span className={styles.railKey}>{c}</span>
                </span>
                <span className={styles.railCount}>{n}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              {categoryName(activeCategory)} · {activeLocale}
            </h3>
            <span className={styles.cardSub}>
              {CATEGORY_BLURB[activeCategory] ??
                "said when a bot is thanked for this kind of help"}
            </span>
          </div>

          {data && data.phrases.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>No phrases yet</div>
              <div className={styles.emptyBody}>
                Bots reaching this category will simply say nothing — the action
                is skipped, not replaced with a phrase from another category or
                another language. Everything else keeps working.
              </div>
              <div className={styles.emptyActions}>
                <Button onClick={() => setImportOpen(true)}>
                  ↑ Import list
                </Button>
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              rows={data?.phrases ?? []}
              empty={isLoading ? "Loading…" : undefined}
            />
          )}

          <div className={styles.cardFoot}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) add.mutate(draft.trim());
              }}
              style={{ display: "flex", gap: 8, flex: 1 }}
            >
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a phrase… {player} and {guild} are substituted"
                aria-label="New phrase"
              />
              <Button type="submit" size="sm" loading={add.isPending}>
                Add
              </Button>
            </form>
          </div>
        </div>
      </div>

      {data && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>Coverage</h3>
            <span className={styles.cardSub}>
              a category with fewer than {THIN_CATEGORY} phrases repeats itself
              and reads as a bot
            </span>
          </div>
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th>Category</th>
                {data.locales.map((l) => (
                  <th key={l}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.categories.map((c) => (
                <tr key={c}>
                  <td>{categoryName(c)}</td>
                  {data.locales.map((l) => {
                    const n = countIn(c, l);
                    const cls =
                      n === 0
                        ? styles.cellNone
                        : n < THIN_CATEGORY
                          ? styles.cellThin
                          : styles.cellOk;
                    return (
                      <td key={l} className={cls}>
                        {n}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.cardFoot}>
            <span>
              An empty cell means a bot in such a guild falls back to the
              default locale — and, if that is empty too, says nothing at all.
            </span>
            <span className={styles.mono}>
              bots.default_locale = {data.locales[0] ?? "—"}
            </span>
          </div>
        </div>
      )}

      {importOpen && data && (
        <ImportModal
          projectID={projectID}
          category={activeCategory}
          locale={activeLocale}
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false);
            void invalidate();
          }}
        />
      )}
    </>
  );
}

/**
 * The import modal. Two modes, because two things actually happen: pasting a
 * list into one category, and dropping in a spreadsheet somebody wrote in four
 * languages at once.
 *
 * The preview is not decoration — it is where a row gets refused, with the
 * reason. A locale nothing maps to would produce phrases no guild can reach.
 */
function ImportModal({
  projectID,
  category,
  locale,
  onClose,
  onDone,
}: {
  projectID: string;
  category: string;
  locale: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { push } = useToast();
  const [mode, setMode] = useState<"lines" | "csv">("lines");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The preview is a round trip per edit, so it waits for the typing to stop.
  // The main path is a paste — one event — but an operator fixing a line by
  // hand would otherwise send the whole body on every character.
  //
  // The generation counter is the part that matters more. Without it a slow
  // response to an early body can land after a fast one to a later body, and
  // the preview table, the button's label and its disabled state would then
  // all describe text that is no longer in the field — while Import sends
  // what IS in the field.
  const previewRun = useRef(0);
  const previewTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(previewTimer.current), []);

  function refreshPreview(next: string, nextMode: "lines" | "csv") {
    setBody(next);
    clearTimeout(previewTimer.current);
    if (!next.trim()) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const run = ++previewRun.current;

    // The timer takes a plain callback: `setTimeout` drops whatever a function
    // returns, so an async one hands its promise — and any rejection the try
    // below could not catch — to nobody.
    async function preview() {
      try {
        const res = await importPhrases(projectID, {
          mode: nextMode,
          body: next,
          category,
          locale,
          preview: true,
        });
        if (run !== previewRun.current) return;
        setPreview(res);
        setPreviewError(null);
      } catch (err) {
        if (run !== previewRun.current) return;
        // Saying why. A preview that failed on the body-size limit and left
        // the Import button greyed out with no message is indistinguishable
        // from a page that has stopped working.
        setPreview(null);
        setPreviewError(botError(err));
      }
    }

    previewTimer.current = setTimeout(() => void preview(), 300);
  }

  async function doImport() {
    setBusy(true);
    try {
      const res = await importPhrases(projectID, {
        mode,
        body,
        category,
        locale,
      });
      push({
        variant: "success",
        message: `Imported ${res.imported} phrases, skipped ${res.skipped}.`,
      });
      onDone();
    } catch (err) {
      push({ variant: "danger", message: botError(err) });
    } finally {
      setBusy(false);
    }
  }

  const skipped = preview?.rows.filter((r) => r.skip) ?? [];

  return (
    <Modal
      open
      title="Import phrases"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={doImport}
            loading={busy}
            disabled={!preview || preview.imported === 0}
          >
            {preview
              ? `Import ${preview.imported} of ${preview.rows.length} rows`
              : "Import"}
          </Button>
        </>
      }
    >
      <div className={styles.importBody}>
        <SegmentedToggle
          value={mode}
          onChange={(m) => {
            setMode(m);
            refreshPreview(body, m);
          }}
          aria-label="Import mode"
          options={[
            { value: "lines", label: "lines" },
            { value: "csv", label: "csv" },
          ]}
        />
        {mode === "lines" ? (
          <>
            <div className={styles.importRow}>
              <span>Category</span>
              <code>{category}</code>
            </div>
            <div className={styles.importRow}>
              <span>Locale</span>
              <code>{locale}</code>
            </div>
            <Textarea
              value={body}
              onChange={(e) => refreshPreview(e.target.value, mode)}
              placeholder={"One phrase per line\n{player} and {guild} are substituted"}
              rows={6}
              aria-label="Phrases, one per line"
            />
          </>
        ) : (
          <Textarea
            value={body}
            onChange={(e) => refreshPreview(e.target.value, mode)}
            placeholder="category,locale,text"
            rows={6}
            aria-label="CSV rows"
          />
        )}

        {preview && preview.rows.length > 0 && (
          <table className={styles.previewTable}>
            <thead>
              <tr>
                <th>Category</th>
                <th>Locale</th>
                <th>Text</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.slice(0, 12).map((r, i) => (
                <tr key={i} className={r.skip ? styles.skipRow : undefined}>
                  <td className={styles.mono}>{r.category}</td>
                  <td className={styles.mono}>{r.locale}</td>
                  <td>{r.skip ? `${r.text} — ${r.reason}` : r.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {previewError && (
          <Alert variant="danger" title="Could not read that">
            {previewError}
          </Alert>
        )}

        {skipped.length > 0 ? (
          <Alert variant="warn" title={`${skipped.length} rows will be skipped`}>
            {skipped[0].reason}
          </Alert>
        ) : (
          <Alert variant="info">
            Imported phrases go live immediately. A line identical to one already
            in the same category and locale is skipped, not duplicated.
          </Alert>
        )}
      </div>
    </Modal>
  );
}
